import PropTypes from 'prop-types';
import './EditHistoryMessage.css';

function diffWords(prev, curr) {
  // Improved diff: word-by-word, then char-by-char for changed words
  const prevWords = prev.split(/(\s+)/);
  const currWords = curr.split(/(\s+)/);
  const result = [];
  let i = 0, j = 0;
  while (i < prevWords.length && j < currWords.length) {
    if (prevWords[i] === currWords[j]) {
      result.push({ text: prevWords[i], type: 'same' });
      i++; j++;
    } else if (prevWords[i].trim() === '' && currWords[j].trim() === '') {
      // Both are whitespace
      result.push({ text: prevWords[i], type: 'same' });
      i++; j++;
    } else {
      // Words differ, do char diff
      const charDiff = diffChars(prevWords[i], currWords[j]);
      result.push(...charDiff);
      i++; j++;
    }
  }
  // Remaining added
  while (j < currWords.length) {
    result.push({ text: currWords[j], type: 'added' });
    j++;
  }
  // Remaining removed
  while (i < prevWords.length) {
    result.push({ text: prevWords[i], type: 'removed' });
    i++;
  }
  return result;
}

function diffChars(prev, curr) {
  // Simple char diff: LCS
  const result = [];
  const a = prev.split('');
  const b = curr.split('');
  const lcs = longestCommonSubsequence(a, b);
  let i = 0, j = 0;
  lcs.forEach(([ai, bj]) => {
    while (i < ai) {
      result.push({ text: a[i], type: 'removed' });
      i++;
    }
    while (j < bj) {
      result.push({ text: b[j], type: 'added' });
      j++;
    }
    // Same char
    result.push({ text: a[i], type: 'same' });
    i++;
    j++;
  });
  // Remaining
  while (i < a.length) {
    result.push({ text: a[i], type: 'removed' });
    i++;
  }
  while (j < b.length) {
    result.push({ text: b[j], type: 'added' });
    j++;
  }
  return result;
}

function longestCommonSubsequence(a, b) {
  // Returns array of [ai, bj] pairs for LCS
  const dp = Array(a.length + 1).fill(null).map(() => Array(b.length + 1).fill(0));
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  // Backtrack
  const lcs = [];
  let i = a.length, j = b.length;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift([i - 1, j - 1]);
      i--; j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  return lcs;
}

const EditHistoryMessage = ({ previousEdit, currentEdit, isOriginal, timestamp, timeOnly }) => {
  let prevContent, currContent;
  // If no previousEdit, show the original message only (no diff)
  if (!previousEdit) {
    prevContent = null;
    currContent = currentEdit.getOriginalContent();
  } else {
    prevContent = isOriginal ? previousEdit.getOriginalContent() : previousEdit.getContent();
    currContent = currentEdit.getContent();
  }

  let dateStr = 'Unknown time';
  if (timestamp) {
    const date = new Date(timestamp);
    if (timeOnly) {
      // Show only HH:MM
      dateStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    } else {
      dateStr = date.toLocaleString();
    }
  }

  const prevBody = prevContent ? (prevContent.body || '') : '';
  const currBody = currContent.body || '';

  let rendered;
  if (!previousEdit) {
    // Just show the original message, no diff
    rendered = <span>{currBody}</span>;
  } else {
    const diff = diffWords(prevBody, currBody);
    // Preprocess the full diff array to mark surrounded whitespace
    function preprocessDiff(diff) {
      return diff.map((p, i, arr) => {
        if (p.type === 'removed' || p.type === 'added') {
          const prev = arr[i - 1];
          const next = arr[i + 1];
          const prevIsSameType = prev && prev.type === p.type;
          const nextIsSameType = next && next.type === p.type;
          if (prevIsSameType && nextIsSameType) {
            return { ...p, surrounded: true };
          }
        }
        return p;
      });
    }
    const processedDiff = preprocessDiff(diff);
    rendered = (() => {
      const arr = [];
      let buffer = [];
      let lastType = null;
      processedDiff.forEach((part) => {
        if (lastType === null) lastType = part.type;
        if (part.type !== lastType || part.type === 'same') {
          if (buffer.length) {
            // Render the buffer as a single span
            const text = buffer.map(p => p.text).join('');
            if (lastType === 'added') {
              arr.push(<span key={arr.length} className='edit-history-message-added'>{text}</span>);
            } else if (lastType === 'removed') {
              arr.push(<span key={arr.length} className='edit-history-message-removed'>{text}</span>);
            } else {
              arr.push(<span key={arr.length}>{text}</span>);
            }
            buffer = [];
          }
        }
        if (part.type === 'added' || part.type === 'removed') {
          buffer.push(part);
        } else {
          arr.push(<span key={arr.length}>{part.text}</span>);
        }
        lastType = part.type;
      });
      if (buffer.length) {
        const text = buffer.map(p => p.text).join('');
        if (lastType === 'added') {
          arr.push(<span key={arr.length} className='edit-history-message-added'>{text}</span>);
        } else if (lastType === 'removed') {
          arr.push(<span key={arr.length} className='edit-history-message-removed'>{text}</span>);
        } else {
          arr.push(<span key={arr.length}>{text}</span>);
        }
      }
      return arr;
    })();
  }

  return (
    <div className="edit-history-message-row">
      <span className="edit-history-message-timestamp">{dateStr}</span>
      <span className="edit-history-message-diff edit-history-message-diff-wrap">{rendered}</span>
    </div>
  );
};

EditHistoryMessage.propTypes = {
  previousEdit: PropTypes.object,
  currentEdit: PropTypes.object.isRequired,
  isOriginal: PropTypes.bool,
  timestamp: PropTypes.number,
  timeOnly: PropTypes.bool,
};

export default EditHistoryMessage;
